import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { find, reject } from 'lodash-es'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useResultsStore } from '@editorStores'
import { getModel, getCalcChecksForElement, getDefaultCheckSettingsForElement } from '@queries'
import { saveCrossSectionsOnElement } from '@mutations'
import { buildErrorMessage } from 'src/constants'

const useUpdateCSandChecks = () => {
  const { projectId } = useParams()

  const queryClient = useQueryClient()

  const { enqueueSnackbar } = useSnackbar()

  const memberCheckSettings = useResultsStore(state => state.memberCheckSettings)
  const setMemberCheckSettings = useResultsStore(state => state.setMemberCheckSettings)

  const structuralChecks = useResultsStore(state => state.structuralChecks)
  const setStructuralChecks = useResultsStore(state => state.setStructuralChecks)

  const supportConfigsForChecks = useResultsStore(state => state.supportConfigsForChecks)
  const setSupportConfigsForChecks = useResultsStore(state => state.setSupportConfigsForChecks)

  return useMutation(
    async (data: { newAssignment: ElementCSAssignment; currentCS: CrossSection }) => {
      let checkSettingsOnSelectedElement = find(
        memberCheckSettings,
        settings => settings.element_guid === data.newAssignment.element_guid,
      )!

      let newSettings = false

      if (data.currentCS.material.kind !== data.newAssignment.element_cs.material.kind) {
        checkSettingsOnSelectedElement = (
          await getDefaultCheckSettingsForElement.request(
            projectId as string,
            data.newAssignment.element_guid,
            data.newAssignment.element_cs,
          )
        ).new_settings

        newSettings = true
      }

      const updatedChecks = await getCalcChecksForElement.request(
        projectId as string,
        data.newAssignment.element_guid,
        checkSettingsOnSelectedElement,
        data.newAssignment.element_cs,
      )

      await saveCrossSectionsOnElement.request(projectId as string, [
        {
          ...data.newAssignment.element_cs,
          element_guid: data.newAssignment.element_guid,
        },
      ])

      return {
        updatedChecks,
        newSettings,
        newSupportConfigsForChecks: updatedChecks.support_configs_for_checks,
      }
    },
    {
      onSuccess: data => {
        const replacedChecks = [
          ...reject(structuralChecks, {
            element_guid: data.updatedChecks.settings.element_guid,
          }),
          ...data.updatedChecks.checks,
        ]
        setStructuralChecks(replacedChecks)

        const replacedSettings = [
          ...reject(memberCheckSettings, {
            element_guid: data.updatedChecks.settings.element_guid,
          }),
          data.updatedChecks.settings,
        ]
        setMemberCheckSettings(replacedSettings)

        const replacedSupportConfigsForChecks: SupportConfigForChecks[] = [
          ...(reject(supportConfigsForChecks, {
            element_guid: data.updatedChecks.settings.element_guid,
          }) as SupportConfigForChecks[]),
          ...(data.newSupportConfigsForChecks ?? []),
        ]
        setSupportConfigsForChecks(replacedSupportConfigsForChecks)

        enqueueSnackbar('Querschnitts-Zuweisung erfolgreich gespeichert', {
          variant: 'success',
        })

        // Keep derived cross-section assignment in sync with the model source-of-truth.
        queryClient.invalidateQueries(getModel.getKey(projectId))

        if (data.newSettings) {
          enqueueSnackbar('Materialtyp wurde geändert, bitte Nachweiseinstellungen genau prüfen', {
            variant: 'info',
          })
        }
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(
          buildErrorMessage(error, 'Fehler beim Speichern der Querschnitts-Zuweisung'),
          { variant: 'error' },
        )

        // Refetch onError to ensure local state is in sync with backend again.
        queryClient.invalidateQueries(getModel.getKey(projectId))
      },
    },
  )
}

export default useUpdateCSandChecks
